import React, { useState } from "react";
import { format, differenceInCalendarDays } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { SearchableSelect } from "./ui/searchable-select";
import { CircleCheck, Info } from "lucide-react";
import { Badge } from "./ui/badge";

function DateField({ label, placeholder, value, onChange, onBlur }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-2">
      {label && <div className="text-sm font-medium">{label}</div>}
      <Popover open={open} onOpenChange={(v) => {
        setOpen(v);
        if (!v) onBlur?.();
      }}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-10 w-full items-center justify-start rounded-lg border border-[#2F7F571A] bg-[#F6F8F7] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F7F57] focus:ring-offset-2"
          >
            {value ? format(value, "MM/dd/yyyy") : (placeholder || "Select date")}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-fit p-2" align="start">
          <Calendar
            mode="single"
            captionLayout="dropdown"
            selected={value}
            onSelect={(d) => {
              if (!d) return;
              onChange(d);
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function Step1InvoiceDetails({
  property,
  setProperty,
  propertyOptions,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  invoiceDate,
  setInvoiceDate,
  dueDate,
  setDueDate,
  invoiceNumber,
  setInvoiceNumber,
  errors,
  touched,
  setTouched,
  isEditing,
  prefillFound,
}) {
  const days =
    startDate && endDate ? differenceInCalendarDays(endDate, startDate) + 1 : 0;

  return (
    <Card className="border-[#2F7F571A] shadow-[0px_1px_2px_0px_#0000000D] overflow-hidden">
      {/* Card Header with step number */}
      <div className="p-6 bg-[#2F7F570D] border-b border-[#2F7F570D] border">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-md bg-[#2F7F57] flex items-center justify-center text-white text-base font-bold flex-shrink-0 mt-0.5">
              1
            </div>
            <div>
              <div className="text-md font-bold text-[#131614]">
                Property & Period
              </div>
              <div className="text-xs text-[#6B7280]">
                Define who is being billed and for when
              </div>
            </div>
          </div>
          {prefillFound && (
            <Badge
              variant="outline"
              className="border-[#DCFCE7] border text-[#15803D] bg-[#F0FDF4] font-bold text-xs px-3 py-1"
            >
              <CircleCheck className="h-4 w-4 mr-1" />
              Record Found & Pre-filled
            </Badge>
          )}
        </div>
      </div>

      <CardContent className="space-y-5 pt-6">
        {/* Row 1: Property & Invoice Date */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <div className="text-sm font-medium">Select Property</div>
            <SearchableSelect
              value={property}
              onValueChange={(v) => {
                setProperty(v);
                setTouched((t) => ({ ...t, property: true }));
              }}
              options={propertyOptions}
              placeholder="Select property"
              className="w-full justify-start text-black text-left"
              disabled={isEditing}
            />
            <div className="text-xs min-h-[16px] text-red-600">
              {touched.property && errors?.property ? errors.property : ""}
            </div>
          </div>
          <div className="space-y-2">
            <DateField
              label="Invoice Date"
              value={invoiceDate}
              onChange={(v) => {
                setInvoiceDate(v);
                setTouched((t) => ({ ...t, invoiceDate: true }));
              }}
              onBlur={() => setTouched((t) => ({ ...t, invoiceDate: true }))}
            />
            <div className="text-xs min-h-[16px] text-red-600">
              {touched.invoiceDate && errors?.invoiceDate ? errors.invoiceDate : ""}
            </div>
          </div>
        </div>

        {/* Row 2: Invoice Number & Billing Period */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
          <div className="md:col-span-6 space-y-2">
            <div className="text-sm font-medium">Invoice Number</div>
            <Input
              value={invoiceNumber}
              onChange={(e) => {
                const raw = String(e.target.value);
                const cleaned = raw.replace(/[^A-Za-z0-9\-_]/g, "");
                setInvoiceNumber(cleaned.slice(0, 64));
                setTouched((t) => ({ ...t, invoiceNumber: true }));
              }}
              onBlur={() => setTouched((t) => ({ ...t, invoiceNumber: true }))}
              className="text-black md:w-[95.5%] w-full"
              placeholder="Enter invoice number"
              disabled={isEditing}
            />
            <div className="text-xs min-h-[16px] text-red-600">
              {touched.invoiceNumber && errors?.invoiceNumber ? errors.invoiceNumber : ""}
            </div>
          </div>

          <div className="md:col-span-6 space-y-2">
            <div className="text-sm font-medium">Billing Period</div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <DateField
                  label=""
                  placeholder="Billing Start Date"
                  value={startDate}
                  onChange={(v) => {
                    setStartDate(v);
                    setTouched((t) => ({ ...t, startDate: true }));
                  }}
                  onBlur={() => setTouched((t) => ({ ...t, startDate: true }))}
                />
              </div>
              <span className="text-sm text-[#6B7280] font-medium">to</span>
              <div className="flex-1">
                <DateField
                  label=""
                  placeholder="Billing End Date"
                  value={endDate}
                  onChange={(v) => {
                    setEndDate(v);
                    setTouched((t) => ({ ...t, endDate: true }));
                  }}
                  onBlur={() => setTouched((t) => ({ ...t, endDate: true }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-xs text-red-600 min-h-[16px]">
                {touched.startDate && errors?.startDate ? errors.startDate : ""}
              </div>
              <div className="text-xs text-red-600 min-h-[16px]">
                {touched.endDate && errors?.endDate ? errors.endDate : ""}
              </div>
            </div>
          </div>
        </div>

        {/* Pay Period Preview */}
        <div className="rounded-xl border border-[#2F7F5733]  bg-[#f5f9f7] p-4">
          <div className="text-xs font-bold text-primary tracking-wide flex items-center">
            <Info className="inline-block mr-1 h-4 w-4" /> PAY PERIOD PREVIEW
          </div>
          <div className="text-sm text-[#131614] mt-1.5">
            {startDate && endDate
              ? `${format(startDate, "MMMM dd, yyyy")} - ${format(
                  endDate,
                  "MMMM dd, yyyy",
                )} (${days} Days)`
              : "Select billing start and billing end date"}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
